import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FlowerButton } from '@/components/ui/FlowerButton';
import { BudgetBar } from '@/components/common/BudgetBar';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CampaignKebabMenu } from '@/components/features/CampaignCard/shared/CampaignKebabMenu';
import { CampaignStatusBadge } from '@/components/features/CampaignCard/shared/CampaignStatusBadge';
import { PlatformBadge } from '@/components/features/CampaignCard/shared/PlatformBadge';
import { toast } from '@/utils';
import { cn } from '@/utils/cn';
import { formatEnumPretty } from '@/utils/formatEnum';
import { formatAccountId } from '@/utils/metaAds';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import {
  useCampaignSettingsMetaAdsStore,
  validateMetaCampaignName,
} from '@/store/campaignSettingsMetaAdsStore';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';
import type { MetaCampaign } from '@/types/metaCampaign';
import { ROAS_CONFIG } from '@/config/constants';
import { computeCampaignRoas, formatRoasDisplay } from '@/utils/roas';
import '@/styles/tooltip.css';

/**
 * Meta campaign card for the unified Account-page grid.
 *
 * Visual parity with `CampaignCard` — same FlowerButton, kebab menu,
 * expandable detail view, Daily Budget + ROAS layout, Performance
 * metrics grid. The kebab menu + status badge are the literal shared
 * components used by Google's card (`./shared/CampaignKebabMenu`,
 * `./shared/CampaignStatusBadge`).
 *
 * Write surface (Phase 1c): inline name edit, BudgetBar with the same
 * eligibility/edit fork as Google, and the FlowerButton toggle for
 * `flouri_optimize_budget`. Status changes (pause / enable / archive)
 * queue through `pendingChangesStore` with `platform: 'meta_ads'` and
 * land via `metaAdsApi.batchUpdate`. Local name + budget edits also
 * write into `campaignSettingsMetaAdsStore` for inline display until
 * the Save dispatches them. Eligibility (CBO + daily budget) gates the
 * FlowerButton + BudgetBar visibility — ABO and lifetime campaigns
 * render the controls disabled with a screen-reader-friendly reason on
 * the button itself.
 *
 * ROAS parity: this card runs the same `computeCampaignRoas` two-tier
 * calc as Google's. `conversions_value` comes from Meta's own
 * `conversion_values` Insights field (advertiser-filtered via Events
 * Manager — see migration 012 and the JSONB rollup in
 * `account_configuration.py:_sum_conversion_field`).
 *
 * Branch selection happens at the *account* level, not per-campaign:
 * the Account-page toggle (`useConversionBasedRoas`) and the per-
 * platform `conversionTrackingAvailable` flag together drive
 * `shouldUseConversionBased` inside `computeCampaignRoas`. A specific
 * card with `conversion_value = $0` while in conversion-based mode
 * will render ROAS = 0/cost (not the click-based estimate) — that's
 * the same behavior as Google. The Account page's "none left behind"
 * gating (require every platform with Flouri-optimized campaigns to
 * have confirmed conversions before enabling conversion-based) is
 * what flips the whole grid to click-based when needed.
 */

interface MetaCampaignCardProps {
  campaign: MetaCampaign;
  /** Optional handler — when archive is confirmed, the parent grid uses
   * this to hide the card immediately (mirrors Google's
   * `CampaignCard.onOptimisticDelete` for the `remove` action). The
   * shared `optimisticDeletions` set in the Account page filters by
   * `campaign_id` agnostically across platforms. Cleared on SaveChangesBar
   * save so the eventual refetch reflects whatever Meta returns. */
  onOptimisticDelete?: (campaignId: string) => void;
  /** Per-click revenue estimate. Shared across platforms (per the
   * "level playing field" decision) — same value passed to
   * `CampaignCard` for Google. */
  revenuePerClick?: number;
  /** Account-level ROAS calculation preference. Driven by the
   * "Conversion Value" / "Click Value" radio on the Account page. */
  useConversionBasedRoas?: boolean;
  /** Whether the conversion-based branch is viable for this card.
   * Computed at the Account page level via the "none left behind"
   * rule: true only if every platform with Flouri-optimized campaigns
   * has at least one campaign reporting `conversions_value > 0`. */
  conversionTrackingAvailable?: boolean;
  /** True when the user explicitly picked click-based via the toggle
   *  (vs being forced into it by missing conversion data). Drives the
   *  disclaimer copy: user-choice → "Click-based · $X/click", forced →
   *  "No conversion data · $X/click". Without this, Meta and Google
   *  cards diverge whenever one platform happens to have conversion
   *  data and the other doesn't — same user choice, different label.
   *  Parity with `CampaignCard`. */
  userPickedClickBased?: boolean;
  /** Optimistic status callback — mirrors Google `CampaignCard.onStatusChange`.
   * Called from the pause/enable/archive confirm handlers BEFORE the
   * Save dispatch; the Account page overlays the value onto the
   * `metaCampaigns` array via `optimisticMetaStatuses` so the card
   * receives the new status via its `campaign` prop on the next
   * render. The card itself reads the prop, so visual feedback is
   * driven by the same React reconciliation path Google uses (not a
   * card-internal store subscription). */
  onStatusChange?: (campaignId: string, newStatus: string) => void;
  /** Cross-platform pool total, computed once at the Account-page
   *  level via `useOptimizedCampaigns().totalCurrentBudget` and passed
   *  down. Earlier each card called the hook directly, re-running
   *  the full cross-store scan on every render (O(N²)). Now the
   *  Account page owns the single call. Used by the pause-math
   *  `totalBeforePause`. */
  poolTotalCurrentBudget?: number;
}

function formatBudget(campaign: MetaCampaign): string {
  // Drop the "(lifetime)" suffix — the surrounding label
  // ("Lifetime Budget" vs "Daily Budget") now carries that
  // distinction, and the suffix doubled it up awkwardly.
  if (campaign.daily_budget != null) {
    return `$${campaign.daily_budget.toFixed(2)}`;
  }
  if (campaign.lifetime_budget != null) {
    return `$${campaign.lifetime_budget.toFixed(2)}`;
  }
  return '$0.00';
}

function budgetLabel(campaign: MetaCampaign): string {
  // Lifetime branch only fires when daily_budget is null AND
  // lifetime_budget is set — campaigns with both null fall through to
  // "Daily Budget" as the default (matches `formatBudget`'s $0.00
  // fallback, which is daily-budget-shaped).
  return campaign.daily_budget == null && campaign.lifetime_budget != null
    ? 'Lifetime Budget'
    : 'Daily Budget';
}

export function MetaCampaignCard({
  campaign,
  onOptimisticDelete,
  revenuePerClick,
  useConversionBasedRoas = false,
  conversionTrackingAvailable = false,
  userPickedClickBased = false,
  onStatusChange,
  poolTotalCurrentBudget = 0,
}: MetaCampaignCardProps) {
  const navigate = useNavigate();

  // A4.2 + Phase 1c: wire to pendingChangesStore (status / rename /
  // budget queue) and the Meta settings store (local UI state for name +
  // budget edits + optimization toggle).
  const queueStatusChange = usePendingChangesStore((s) => s.queueStatusChange);
  const queueCampaignChange = usePendingChangesStore((s) => s.queueCampaignChange);
  const removeCampaignChange = usePendingChangesStore((s) => s.removeCampaignChange);
  const getPendingStatusChange = usePendingChangesStore((s) => s.getPendingStatusChange);
  const registerDiscardCallback = usePendingChangesStore((s) => s.registerDiscardCallback);
  const unregisterDiscardCallback = usePendingChangesStore((s) => s.unregisterDiscardCallback);
  const setManualName = useCampaignSettingsMetaAdsStore((s) => s.setManualName);
  const setManualBudget = useCampaignSettingsMetaAdsStore((s) => s.setManualBudget);
  const toggleCampaignOptimization = useCampaignSettingsMetaAdsStore((s) => s.toggleCampaignOptimization);
  const updateCampaignStatus = useCampaignSettingsMetaAdsStore((s) => s.updateCampaignStatus);
  const clearNameChange = useCampaignSettingsMetaAdsStore((s) => s.clearNameChange);
  const clearBudgetChange = useCampaignSettingsMetaAdsStore((s) => s.clearBudgetChange);
  // Allocatable-budget adjustment on pause/enable mirrors Google's
  // CampaignCard.handleConfirmPause/Enable. The cross-platform pool
  // total comes in via prop from the Account page, which calls
  // `useOptimizedCampaigns()` once and forwards the total to every
  // card. Earlier each card called the hook directly — the full
  // cross-store scan ran N times per render (one per card). Lifted
  // to one call shared by all cards.
  const totalBudget = useAccountSettingsStore((s) => s.totalBudget);
  const setTotalBudget = useAccountSettingsStore((s) => s.setTotalBudget);
  const totalCurrentBudget = poolTotalCurrentBudget;
  // Read manualName for display so a queued rename is visible to the
  // user immediately, not just after the post-save refetch.
  const settings = useCampaignSettingsMetaAdsStore(
    React.useCallback(
      (state) => state.campaignSettings.get(campaign.campaign_id),
      [campaign.campaign_id],
    ),
  );
  const displayName = settings?.manualName ?? campaign.campaign_name;

  // Effective status = pending Meta status change (optimistic, even
  // before SaveChangesBar fires) OR the raw prop. Without this, the
  // kebab keeps showing "Pause campaign" after the user already queued
  // a pause + saved — the prop won't update until the React Query
  // refetch lands.
  //
  // Round-3 of the platform discriminator made this call platform-aware,
  // so a colliding numeric ID on a Google card can't bleed into this
  // Meta card.
  // useAccountChanges destructures `usePendingChangesStore()` (whole
  // state), so the Account page re-renders on every queue mutation
  // and the card re-renders with it. No card-internal subscription
  // needed — `getPendingStatusChange` reads current state on each
  // render and returns the right tuple-aware (campaign + platform)
  // match.
  const pendingMetaChange = getPendingStatusChange(
    'campaign', campaign.campaign_id, 'meta_ads',
  );
  const STATUS_BY_ACTION: Record<'pause' | 'enable' | 'archive', string> = {
    pause: 'PAUSED',
    enable: 'ACTIVE',
    archive: 'ARCHIVED',
  };
  const optimisticStatus = pendingMetaChange
    ? STATUS_BY_ACTION[pendingMetaChange.action as 'pause' | 'enable' | 'archive']
    : null;
  // In-progress label parallel to Google's CampaignCard:702-712
  // "Pausing..." / "Enabling..." / "Removing..." indicator. Without an
  // explicit verb the user only sees the same status badge color and
  // can't tell whether their click registered.
  const inProgressLabel = pendingMetaChange
    ? (
        pendingMetaChange.action === 'pause' ? 'Pausing...' :
        pendingMetaChange.action === 'enable' ? 'Enabling...' :
        pendingMetaChange.action === 'archive' ? 'Archiving...' :
        'Pending...'
      )
    : null;

  const [showMenu, setShowMenu] = React.useState(false);
  const [editingName, setEditingName] = React.useState(false);
  const [nameValue, setNameValue] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [showPauseDialog, setShowPauseDialog] = React.useState(false);
  const [showEnableDialog, setShowEnableDialog] = React.useState(false);
  // "Archive" — Meta's terminal state. (Meta has no DELETE verb per §8.1.)
  const [showArchiveDialog, setShowArchiveDialog] = React.useState(false);

  const menuRef = React.useRef<HTMLDivElement>(null);
  const collapsedMenuRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const editStartTimeRef = React.useRef<number>(0);
  const lastToggleTimeRef = React.useRef<number>(0);

  // Phase 1c — read the toggle from the Meta settings store (mirrors
  // Google's CampaignCard pattern at CampaignCard/index.tsx:98). Falls
  // back to the campaign payload's `flouri_optimize_budget` when the
  // store hasn't been seeded yet (first paint before
  // initializeCampaignSettings runs). Without the fallback, the toggle
  // would flicker OFF on first render of a campaign that's actually ON
  // in the saved config.
  const isOptimizationEnabled =
    settings?.isOptimizationEnabled ?? campaign.flouri_optimize_budget;

  // Phase 1 eligibility: CBO + daily-budget only. ABO (ad-set-level
  // budget) and lifetime budgets aren't candidates for the simplex
  // allocator (see META_BUDGET_OPTIMIZATION_ACTIVATION.md §A). Filter
  // these out at the FlowerButton + BudgetBar surface so the user
  // can't toggle on a campaign the optimizer would silently exclude.
  const isLifetimeBudget = campaign.daily_budget === null;
  const isAbo = campaign.is_cbo !== true;
  const isEligibleForOptimization = !isLifetimeBudget && !isAbo;
  // Lifetime takes precedence — it's the unambiguous reason. Then ABO.
  // Both result in the same outcome (toggle disabled), so the tooltip
  // copy is the only place the distinction surfaces.
  const optimizationIneligibilityReason = isLifetimeBudget
    ? 'Lifetime-budget campaigns can’t be Flouri-optimized.'
    : isAbo
    ? 'Ad-set-level (ABO) campaigns can’t be Flouri-optimized.'
    : null;

  // Current edit value with the same coalescing pattern as Google's
  // CampaignCard. Null daily_budget (lifetime campaigns) is filtered
  // upstream by `isEligibleForOptimization`, so the `?? 0` fallback
  // only matters for the disabled-tooltip branch where the value
  // never reaches the BudgetBar.
  // Budget baseline preference order:
  //   1. `settings.manualBudget` — user's in-flight staged edit
  //   2. `settings.currentBudget` — store baseline, includes the
  //      post-save promotion done by pendingChangesStore (so the new
  //      value renders BEFORE the React Query refetch completes)
  //   3. `campaign.daily_budget` — live prop fallback for the brief
  //      window before `initializeCampaignSettings` seeds the store
  // `??` (not `||`) throughout — 0 is a valid budget value.
  const settingsBaselineBudget = settings?.currentBudget ?? campaign.daily_budget ?? 0;
  const currentManualBudget = settings?.manualBudget ?? settingsBaselineBudget;
  // Epsilon-compare matches `setManualBudget`'s drop-from-Set rule
  // (Math.abs(...) > 0.001). Strict `!==` could mark a 0.0001-cent
  // float-noise diff as a staged edit even after the dirty Set
  // cleared, leaving "(Save Changes to apply)" visible with nothing
  // actually pending.
  const hasStagedBudgetEdit =
    settings?.manualBudget != null &&
    Math.abs(settings.manualBudget - settingsBaselineBudget) > 0.001;

  // Accessible label + visual tooltip for the FlowerButton. FlowerButton
  // spreads `...props` AFTER its hardcoded `aria-label`, so passing
  // aria-label here overrides the generic "Open flower" / "Close flower"
  // default. Screen readers announce the campaign name + the disabled
  // reason without relying on the `title` attribute (which support for
  // varies wildly across assistive tech). `title` is still set so
  // sighted mouse users get the same hover tooltip.
  const flowerAriaLabel = !isEligibleForOptimization
    ? `Flouri optimization for ${displayName} — disabled: ${optimizationIneligibilityReason ?? 'not eligible'}`
    : isOptimizationEnabled
    ? `Disable Flouri optimization for ${displayName}`
    : `Enable Flouri optimization for ${displayName}`;

  // Shared ROAS calc — same two-tier logic the Google card uses. See
  // `src/utils/roas.ts` for the decision tree. The Account-page toggle +
  // "none left behind" gating drive `useConversionBasedRoas` /
  // `conversionTrackingAvailable`; this card just consumes them.
  const { roas, isUsingClickBasedRoas } = computeCampaignRoas({
    cost: campaign.metrics.cost,
    clicks: campaign.metrics.clicks,
    conversionsValue: campaign.metrics.conversions_value,
    revenuePerClick,
    useConversionBasedRoas,
    conversionTrackingAvailable,
  });
  const shouldUseConversionBased = useConversionBasedRoas && conversionTrackingAvailable;
  // Prefer the store's `currentBudget` over the live prop's
  // `daily_budget` for daily campaigns. The store baseline reflects
  // the post-save promotion done by pendingChangesStore (immediately
  // after a successful /meta/batch-update budget save); the prop
  // doesn't refresh until the next React Query refetch completes.
  // Without this, the card flashes the old value between save success
  // and refetch landing. Lifetime campaigns keep the prop path —
  // `currentBudget` is 0 for them and lifetime_budget lives on the prop.
  const budgetDisplay = settings?.isDailyBudget
    ? `$${(settings.currentBudget ?? 0).toFixed(2)}`
    : formatBudget(campaign);
  // Label (daily vs lifetime) is set by the API, not editable, so
  // reading it from the prop is correct. Kept on the prop path.
  const budgetLabelText = budgetLabel(campaign);

  const performanceMetrics = [
    { label: 'Clicks', value: campaign.metrics.clicks.toLocaleString() },
    { label: 'Impressions', value: campaign.metrics.impressions.toLocaleString() },
    { label: 'CTR', value: `${(campaign.metrics.ctr ?? 0).toFixed(1)}%` },
    { label: 'CPC', value: `$${(campaign.metrics.cpc ?? 0).toFixed(2)}` },
  ];

  // ── Handlers ────────────────────────────────────────────────────────
  const handleOptimizationToggle = (checked: boolean) => {
    // Defense in depth: render disables `onToggle` for ineligible
    // campaigns, but FlowerButton can be invoked via keyboard too —
    // refuse mutations for ABO / lifetime so the toggle UI can never
    // commit a value the optimizer would silently exclude.
    if (!isEligibleForOptimization) {
      if (optimizationIneligibilityReason) {
        toast.info(optimizationIneligibilityReason);
      }
      return;
    }
    toggleCampaignOptimization(campaign.campaign_id, checked);
  };

  const handleBudgetChange = (newBudget: number) => {
    setManualBudget(campaign.campaign_id, newBudget);
    // Queue alongside the local store write, matching the handleSaveName
    // flow above. The pendingChangesStore Meta partition picks `budget`
    // entries up and ships them in the unified /meta/batch-update.
    // `oldValue` reads from `settings.currentBudget` (store baseline,
    // includes post-save promotion) — the prop's `daily_budget` can be
    // stale after a successful batch update, which would record a
    // misleading delta and break a later reset-to-original comparison.
    queueCampaignChange({
      campaignId: campaign.campaign_id,
      property: 'budget',
      oldValue: settings?.currentBudget ?? campaign.daily_budget ?? 0,
      newValue: newBudget,
      platform: 'meta_ads',
    });
  };

  const handleResetBudget = () => {
    // "Original" is the store baseline (`currentBudget`), not the
    // prop's `daily_budget`. After a save the store reflects the
    // promoted value before the refetch lands; resetting against the
    // stale prop would snap back to the pre-save value, which is
    // worse than not resetting at all.
    const original = settings?.currentBudget ?? campaign.daily_budget ?? 0;
    setManualBudget(campaign.campaign_id, original);
    // The setManualBudget epsilon-compare will drop the entry from
    // manualBudgetChanges; remove it from the pending queue too so
    // a Reset doesn't leave a stale queued no-op around.
    removeCampaignChange(campaign.campaign_id, 'budget', 'meta_ads');
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (
      (e.target as HTMLElement).closest('[data-flower-button]') ||
      (e.target as HTMLElement).closest('[data-kebab-menu]') ||
      editingName
    ) {
      return;
    }
    navigate(`/meta-campaign/${campaign.campaign_id}`);
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (editingName) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/meta-campaign/${campaign.campaign_id}`);
    }
  };

  const handleBodyClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[data-kebab-menu]')
    ) return;
    const now = Date.now();
    if (now - lastToggleTimeRef.current < 300) return;
    lastToggleTimeRef.current = now;
    e.stopPropagation();
    setIsExpanded((v) => !v);
  };

  const handleBodyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const now = Date.now();
      if (now - lastToggleTimeRef.current < 300) return;
      lastToggleTimeRef.current = now;
      setIsExpanded((v) => !v);
    }
  };

  const handleEditName = () => {
    setNameValue(campaign.campaign_name);
    editStartTimeRef.current = Date.now();
    setEditingName(true);
  };

  const handleSaveName = () => {
    if (Date.now() - editStartTimeRef.current < 100) return;
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== campaign.campaign_name) {
      // Validate inline against the same §8.4 rules the backend enforces
      // on RenameMetaEntityRequest. Without this gate, queueing an
      // invalid name would surface a success toast and then fail
      // server-side when SaveChangesBar runs — exactly the
      // "looks-saved-but-isn't" trap Copilot flagged.
      const validation = validateMetaCampaignName(trimmed);
      if (!validation.isValid) {
        // Don't persist the invalid value to the Meta store. Writing it
        // there would corrupt `displayName` (which reads
        // `settings?.manualName ?? campaign.campaign_name`) and survive
        // the user hitting Escape — blanking out the title for an
        // empty input or rendering control chars for malformed input.
        // The toast is sufficient feedback; local `nameValue` state
        // keeps the input itself in sync until the editor closes.
        toast.error(validation.error ?? 'Name is invalid.');
        return;
      }
      // Mirror Google's pattern: write to the local settings store for
      // inline display + queue the change in pendingChangesStore so the
      // SaveChangesBar dispatches it to /meta/batch-update on save.
      setManualName(campaign.campaign_id, validation.sanitizedName);
      queueCampaignChange({
        campaignId: campaign.campaign_id,
        property: 'name',
        oldValue: campaign.campaign_name,
        newValue: validation.sanitizedName,
        platform: 'meta_ads',
      });
      toast.success(`Queued: Rename "${campaign.campaign_name}". Click Save to apply.`);
    } else if (trimmed === campaign.campaign_name && settings?.manualName) {
      // Revert-to-original: the user re-edited and went back to the
      // server name (or clicked Edit Name and immediately saved). If
      // there's a previously-queued rename, the next save would still
      // dispatch it — renaming to whatever was typed before — even
      // though the user clearly meant to undo. Drop the queued rename
      // and clear the in-flight tracking in both stores.
      removeCampaignChange(campaign.campaign_id, 'name', 'meta_ads');
      clearNameChange(campaign.campaign_id);
      useCampaignSettingsMetaAdsStore.getState().setCampaignSettings(
        campaign.campaign_id,
        { manualName: null, nameValidationError: null },
      );
      toast.success(`Rename canceled for "${campaign.campaign_name}".`);
    }
    setEditingName(false);
  };

  const handleCancelEditName = () => {
    setEditingName(false);
    setNameValue('');
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditName();
    }
  };

  const handleEditNameClick = () => {
    setShowMenu(false);
    handleEditName();
  };

  const handlePauseClick = () => {
    setShowMenu(false);
    setShowPauseDialog(true);
  };
  const handleConfirmPause = () => {
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'pause',
      entityName: campaign.campaign_name,
      platform: 'meta_ads',
    });
    // Optimistic status overlay — Account page applies it to the
    // `campaign` prop on next render, giving the same React-driven
    // visual feedback Google has.
    onStatusChange?.(campaign.campaign_id, 'PAUSED');

    // Three-scenario allocatable-budget adjustment, mirroring Google's
    // CampaignCard.handleConfirmPause exactly. Only triggers for
    // campaigns that ARE currently in the optimization pool
    // (flouri-optimized AND ACTIVE) — those are the ones whose
    // contribution to the global allocatable needs to be removed.
    // settings.campaignStatus reflects in-flight state (e.g., a
    // pending pause from an earlier session); fall back to the API
    // status prop. Status is updated AFTER the math runs so
    // `totalCurrentBudget` (captured before this render's effects)
    // still reflects the pre-pause pool.
    const currentStatus = settings?.campaignStatus || campaign.campaign_status;
    if (isOptimizationEnabled && currentStatus === 'ACTIVE') {
      const currentAllocatable = totalBudget;
      // Prefer staged `manualBudget` so the allocatable decrement
      // matches what `useOptimizedCampaigns` already counted in
      // `totalCurrentBudget`. A campaign whose budget was bumped via
      // Apply Optimization but not yet saved would otherwise be
      // subtracted at the pre-bump baseline. `??` not `||` to keep
      // 0 as a valid value. Symmetric to the Google card's pause math.
      const pausedCampaignBudget =
        settings?.manualBudget ?? settings?.currentBudget ?? campaign.daily_budget ?? 0;
      const totalBeforePause = totalCurrentBudget;
      const remainingBudget = totalBeforePause - pausedCampaignBudget;

      let newAllocatable: number;
      if (currentAllocatable >= totalBeforePause) {
        // Scenario 1: allocatable covered or exceeded total — subtract
        // the paused campaign's contribution.
        newAllocatable = currentAllocatable - pausedCampaignBudget;
      } else {
        // Scenarios 2 & 3 combined via `min`:
        //   - remainingBudget <= currentAllocatable → return remaining
        //   - currentAllocatable < remainingBudget → keep currentAllocatable
        newAllocatable = Math.min(currentAllocatable, remainingBudget);
      }

      updateCampaignStatus(campaign.campaign_id, 'PAUSED');
      setTotalBudget(newAllocatable);
    } else {
      // Not in the pool — just record the status change, no budget math.
      updateCampaignStatus(campaign.campaign_id, 'PAUSED');
    }

    toast.success(`Queued: Pause "${campaign.campaign_name}". Click Save to apply.`);
    setShowPauseDialog(false);
  };

  const handleEnableClick = () => {
    setShowMenu(false);
    setShowEnableDialog(true);
  };
  const handleConfirmEnable = () => {
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'enable',
      entityName: campaign.campaign_name,
      platform: 'meta_ads',
    });
    onStatusChange?.(campaign.campaign_id, 'ACTIVE');

    // Mirror Google's CampaignCard.handleConfirmEnable: update status
    // first (the inverse of pause — adding a fixed campaign budget is
    // safe to compute after the status change). If the campaign is
    // flouri-optimized AND was NOT currently ACTIVE, its budget joins
    // the pool, so the global allocatable should expand by its budget.
    updateCampaignStatus(campaign.campaign_id, 'ACTIVE');
    const currentStatus = settings?.campaignStatus || campaign.campaign_status;
    if (isOptimizationEnabled && currentStatus !== 'ACTIVE') {
      // Symmetric to the pause math + Google card: prefer staged
      // `manualBudget` so the allocatable increment matches what
      // `useOptimizedCampaigns` will count post-enable.
      const campaignBudget =
        settings?.manualBudget ?? settings?.currentBudget ?? campaign.daily_budget ?? 0;
      setTotalBudget(totalBudget + campaignBudget);
    }

    toast.success(`Queued: Enable "${campaign.campaign_name}". Click Save to apply.`);
    setShowEnableDialog(false);
  };

  // Meta has no DELETE verb — archive is the terminal state per §8.1.
  const handleArchiveClick = () => {
    setShowMenu(false);
    setShowArchiveDialog(true);
  };
  const handleConfirmArchive = () => {
    queueStatusChange({
      entityType: 'campaign',
      entityId: campaign.campaign_id,
      campaignId: campaign.campaign_id,
      action: 'archive',
      entityName: campaign.campaign_name,
      platform: 'meta_ads',
    });
    onStatusChange?.(campaign.campaign_id, 'ARCHIVED');
    // Update the Meta store's status so `useOptimizedCampaigns` filters
    // the archived campaign out of the pool immediately. No budget math:
    // archive ≠ pause for the optimizer — the campaign is being removed
    // from the grid entirely via `optimisticDeletions` below, so the
    // simpler removal path applies. Mirrors Google's
    // `handleConfirmDelete` shape (which is `remove` action, also no
    // budget math).
    updateCampaignStatus(campaign.campaign_id, 'ARCHIVED');

    // Optimistically remove from UI — analogous to Google's Delete flow
    // at CampaignCard:277-279. The Account page's `optimisticDeletions`
    // set filters by campaign_id agnostically, so the same handler works
    // for both platforms. State is cleared on SaveChangesBar save so the
    // eventual refetch is authoritative.
    if (onOptimisticDelete) {
      onOptimisticDelete(campaign.campaign_id);
    }

    toast.success(`Queued: Archive "${campaign.campaign_name}". Click Save to apply.`);
    setShowArchiveDialog(false);
  };

  // Register a discard callback so the SaveChangesBar's "Discard" CTA
  // resets this card's Meta-store in-flight state (manualName +
  // nameValidationError + manualNameChanges Set membership) AND the
  // local edit-mode React state. Without it, a discard would leave
  // the typed-but-unsaved name on the store entry, and the next
  // refetch's `initializeCampaignSettings` (round-3 preservation
  // policy) would still see the stale value — but only as long as
  // `manualNameChanges` contained the id. Clearing both halves keeps
  // the round-3 policy honest.
  const discardCallbackKey = `meta-campaign-card:${campaign.campaign_id}`;
  React.useEffect(() => {
    const restore = () => {
      clearNameChange(campaign.campaign_id);
      clearBudgetChange(campaign.campaign_id);
      // Reset the per-entry edits so the next render reads through to
      // server truth (campaign.campaign_name / campaign.daily_budget).
      // Toggle reset happens in the Meta store's discardChanges (the
      // useAccountChanges hook calls it for the whole store), not here
      // — same shape as the rename + budget reset above which the
      // store's discardChanges also handles. We mirror the store
      // operations on this entry so consumers reading the store
      // outside the Account-page path (e.g. card preview) also see
      // the reset.
      useCampaignSettingsMetaAdsStore.getState().setCampaignSettings(
        campaign.campaign_id,
        { manualName: null, nameValidationError: null, manualBudget: null },
      );
      // Drop any queued Meta changes for this campaign so a discard
      // doesn't leave a stale queued op (mirroring the post-success
      // cleanup in pendingChangesStore).
      removeCampaignChange(campaign.campaign_id, 'name', 'meta_ads');
      removeCampaignChange(campaign.campaign_id, 'budget', 'meta_ads');
      // Local edit-mode reset so the input isn't stuck open.
      setEditingName(false);
      setNameValue('');
    };
    registerDiscardCallback(discardCallbackKey, restore);
    return () => unregisterDiscardCallback(discardCallbackKey);
  }, [
    campaign.campaign_id, discardCallbackKey,
    clearNameChange, clearBudgetChange,
    registerDiscardCallback, unregisterDiscardCallback,
    removeCampaignChange,
  ]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideExpandedMenu = menuRef.current && menuRef.current.contains(target);
      const isInsideCollapsedMenu = collapsedMenuRef.current && collapsedMenuRef.current.contains(target);
      if (!isInsideExpandedMenu && !isInsideCollapsedMenu) setShowMenu(false);
    };
    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenu]);

  // Collapse the expanded card when the user clicks anywhere outside it
  React.useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isExpanded]);

  const showExpanded = isExpanded || editingName;

  const cardStatus = optimisticStatus ?? campaign.campaign_status;

  return (
    <div className="relative" ref={cardRef}>
      {/* Collapsed Card - Always Visible */}
      <div
        className={cn(
          'bg-white rounded-lg shadow-sm border transition-all duration-200',
          isOptimizationEnabled && cardStatus === 'PAUSED'
            ? 'border-amber-300 bg-amber-50 shadow-md'
            : isOptimizationEnabled
            ? 'border-blue-300 bg-blue-50 shadow-md'
            : 'border-gray-200 hover:shadow-md',
        )}
      >
        <div className="p-6 flex flex-col">
          {/* Header — FlowerButton + Name + Kebab */}
          <div
            className="flex items-start space-x-3 mb-4"
            onClick={handleHeaderClick}
            onKeyDown={handleHeaderKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`View campaign: ${displayName}`}
          >
            <div
              className={cn(
                'mt-1',
                !isEligibleForOptimization && 'tooltip-container',
              )}
              data-flower-button
              onClick={(e) => e.stopPropagation()}
            >
              <FlowerButton
                size={25}
                isOpen={isOptimizationEnabled}
                onToggle={handleOptimizationToggle}
                showCenterDot={false}
                disabled={!isEligibleForOptimization}
                aria-disabled={!isEligibleForOptimization}
                aria-label={flowerAriaLabel}
                title={optimizationIneligibilityReason ?? undefined}
              />
              {!isEligibleForOptimization && optimizationIneligibilityReason && (
                <span className="tooltip-text tooltip-text-left tooltip-text-below">
                  {optimizationIneligibilityReason}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              {editingName ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="h-[1.75rem]">
                    <input
                      type="text"
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onKeyDown={handleNameKeyDown}
                      onBlur={handleSaveName}
                      className="w-full px-2 py-0.5 text-lg font-semibold border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      aria-label="Edit campaign name"
                      aria-describedby={`meta-campaign-name-help-${campaign.campaign_id}`}
                    />
                    <span id={`meta-campaign-name-help-${campaign.campaign_id}`} className="sr-only">
                      Press Enter to save, Escape to cancel
                    </span>
                  </div>
                  <div className="h-1" />
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="h-[1.75rem]">
                      <h3 className="text-lg font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
                        {displayName}
                      </h3>
                    </div>
                    {/* Always reserve space for modification notice — mirrors
                        the parity treatment in Google's CampaignCard so the
                        user gets the same visual confirmation that a queued
                        rename will fire on the next Save. */}
                    <div className="h-1">
                      {settings?.manualName && settings.manualName !== campaign.campaign_name && (
                        <div className="text-xs text-amber-600 font-medium">(name modified. Press Save to confirm.)</div>
                      )}
                    </div>
                  </div>
                  {/* Kebab menu */}
                  <div
                    className="relative flex-shrink-0"
                    ref={collapsedMenuRef}
                    data-kebab-menu
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setShowMenu(!showMenu);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Campaign options"
                      aria-label="Campaign options menu"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <circle cx="12" cy="6" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="12" cy="18" r="2" />
                      </svg>
                    </button>
                    {showMenu && (
                      <CampaignKebabMenu
                        status={cardStatus}
                        onEditName={handleEditNameClick}
                        onPause={handlePauseClick}
                        onEnable={handleEnableClick}
                        onDelete={handleArchiveClick}
                        deleteLabel="Archive campaign"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Horizontal divider */}
          <div className="border-t border-gray-200 mb-4" />

          {/* Collapsed View — Platform | Daily Budget | ROAS.
              ROAS column is wired but the value is "—" until Meta
              conversion tracking (Pixel / Conversions API) ships as
              its own workstream. Keeping the slot here so the layout
              matches Google cards and the swap is one line when
              tracking lands. */}
          <div
            className="grid grid-cols-[auto_1fr_1fr] gap-4 items-end"
            onClick={handleBodyClick}
            onKeyDown={handleBodyKeyDown}
            role="button"
            tabIndex={0}
            aria-label={isExpanded ? 'Collapse card details' : 'Expand card details'}
            aria-expanded={isExpanded}
          >
            <div className="flex items-center justify-center pb-1">
              <PlatformBadge platform={campaign.platform_source} />
            </div>
            <div className="relative">
              <p className="text-sm text-gray-500 mb-2">{budgetLabelText}</p>
              <p className="text-xl font-bold text-gray-900">
                {/* When the user has a staged budget edit and the
                    BudgetBar is the active editor (i.e. campaign isn't
                    currently optimizing-while-ACTIVE), show the staged
                    value so the collapsed read reflects the in-flight
                    change. Otherwise fall through to `budgetDisplay`
                    (which handles the lifetime suffix + null branches). */}
                {hasStagedBudgetEdit && !(isOptimizationEnabled && cardStatus === 'ACTIVE')
                  ? `$${currentManualBudget.toFixed(2)}`
                  : budgetDisplay}
              </p>
              {/* Parity with Google's CampaignCard:547-551 — absolutely
                  positioned so the indicator overlays the bottom padding
                  rather than growing the column (which would push the
                  ROAS column down with `items-end` on the grid). */}
              {hasStagedBudgetEdit && !(isOptimizationEnabled && cardStatus === 'ACTIVE') && (
                <span className="absolute left-0 top-full mt-0.5 text-xs text-amber-600 font-medium whitespace-nowrap">
                  (<span className="font-bold">Save Changes</span> to apply)
                </span>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">ROAS</p>
              <p className="text-xl font-bold text-gray-900">{formatRoasDisplay(roas)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Card Overlay — appears when the card body is clicked */}
      {showExpanded && (
        <div className="absolute top-0 left-0 right-0 z-30 animate-expand-card">
          <div
            className={cn(
              'bg-white rounded-lg shadow-xl border-2 overflow-hidden',
              isOptimizationEnabled && cardStatus === 'PAUSED'
                ? 'border-amber-400 bg-amber-50'
                : isOptimizationEnabled
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300',
            )}
          >
            <div className="p-6 flex flex-col animate-fade-in">
              {/* Expanded Header */}
              <div
                className="flex items-start space-x-3 mb-4 cursor-pointer"
                onClick={handleHeaderClick}
                onKeyDown={handleHeaderKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`View campaign: ${displayName}`}
              >
                <div
                  className={cn(
                    'mt-1',
                    !isEligibleForOptimization && 'tooltip-container',
                  )}
                  data-flower-button
                  onClick={(e) => e.stopPropagation()}
                >
                  <FlowerButton
                    size={25}
                    isOpen={isOptimizationEnabled}
                    onToggle={handleOptimizationToggle}
                    showCenterDot={false}
                    disabled={!isEligibleForOptimization}
                    aria-disabled={!isEligibleForOptimization}
                    aria-label={flowerAriaLabel}
                    title={optimizationIneligibilityReason ?? undefined}
                  />
                  {!isEligibleForOptimization && optimizationIneligibilityReason && (
                    <span className="tooltip-text tooltip-text-left tooltip-text-below">
                      {optimizationIneligibilityReason}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {/* Campaign title — heuristic two-line fork: names
                      longer than ~30 chars typically wrap at the card
                      width, so grow the slot to a full 2-line height
                      to keep breathing room above the divider. Single-
                      line names keep the original 1.75rem slot
                      untouched. */}
                  <div className={displayName.length > 30 ? 'h-[3.5rem]' : 'h-[1.75rem]'}>
                    {editingName ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={nameValue}
                          onChange={(e) => setNameValue(e.target.value)}
                          onKeyDown={handleNameKeyDown}
                          onBlur={handleSaveName}
                          className="w-full px-2 py-1 text-lg font-semibold border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                          aria-label="Edit campaign name"
                          aria-describedby={`meta-campaign-name-help-expanded-${campaign.campaign_id}`}
                        />
                        <span id={`meta-campaign-name-help-expanded-${campaign.campaign_id}`} className="sr-only">
                          Press Enter to save, Escape to cancel
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 group w-full">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                            {displayName}
                          </h3>
                          {/* Modification notice — parity with the collapsed
                              view above + Google's CampaignCard. */}
                          {settings?.manualName && settings.manualName !== campaign.campaign_name && (
                            <div className="text-xs text-amber-600 font-medium">(name modified. Press Save to confirm.)</div>
                          )}
                        </div>
                        <div
                          className="relative flex-shrink-0"
                          ref={menuRef}
                          data-kebab-menu
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setShowMenu(!showMenu);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Campaign options"
                            aria-label="Campaign options menu"
                          >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <circle cx="12" cy="6" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="18" r="2" />
                            </svg>
                          </button>
                          {showMenu && (
                            <CampaignKebabMenu
                              status={cardStatus}
                              onEditName={handleEditNameClick}
                              onPause={handlePauseClick}
                              onEnable={handleEnableClick}
                              onDelete={handleArchiveClick}
                              deleteLabel="Archive campaign"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="h-1" />
                </div>
              </div>

              {/* Divider between navigate header and collapse body */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Body — clicking collapses */}
              <div
                className="cursor-pointer"
                onClick={handleBodyClick}
                onKeyDown={handleBodyKeyDown}
                role="button"
                tabIndex={0}
                aria-label="Collapse card details"
                aria-expanded={true}
              >
                {/* Campaign details — mirrors Google's order:
                    Account / Campaign Type / [reserved space] / Status /
                    Budget Strategy. The reserved-height div keeps cards
                    visually aligned regardless of whether bid_strategy
                    is present, matching how Google's card reserves
                    space for its Bidding strategy line. Meta's
                    `objective` slots into "Campaign Type" because
                    that's the semantically equivalent label (Sales,
                    Traffic, Awareness — vs Google's Search, Display,
                    PMax). */}
                <div className="mb-4 pl-9">
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Platform:</span> Meta Ads
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Account:</span>{' '}
                    {formatAccountId(campaign.meta_ad_account_id)}
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    <span className="font-semibold text-gray-700">Campaign Type:</span>{' '}
                    {campaign.objective ? formatEnumPretty(campaign.objective) : 'Meta Ads'}
                  </p>
                  {/* Bid strategy — reserved space (matches Google's pattern) */}
                  <div className="h-5">
                    {campaign.bid_strategy && (
                      <p className="text-sm text-gray-500 line-clamp-1">
                        <span className="font-semibold text-gray-700">Bid Strategy:</span>{' '}
                        {formatEnumPretty(campaign.bid_strategy)}
                        {campaign.buying_type ? ` (${formatEnumPretty(campaign.buying_type)})` : ''}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-gray-700">Status:</span>{' '}
                    {inProgressLabel ? (
                      <span
                        className={`ml-1 px-2 py-0.5 rounded text-xs font-medium animate-pulse ${
                          pendingMetaChange?.action === 'enable'
                            ? 'bg-green-100 text-green-800'
                            : pendingMetaChange?.action === 'archive'
                            ? 'bg-gray-200 text-gray-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {inProgressLabel}
                      </span>
                    ) : (
                      <CampaignStatusBadge status={cardStatus} className="ml-1" />
                    )}
                  </div>
                  {/* Budget Strategy — mirrors Google's Flouri-Optimized
                      vs Manual indicator. Meta's "running" state is
                      ACTIVE (Google's is ENABLED) — same semantics. */}
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-gray-700">Budget Strategy:</span>{' '}
                    <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                      isOptimizationEnabled && cardStatus === 'ACTIVE'
                        ? 'bg-blue-100 text-blue-800'
                        : isOptimizationEnabled && cardStatus !== 'ACTIVE'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isOptimizationEnabled && cardStatus === 'ACTIVE'
                        ? '✓ Flouri Optimized'
                        : isOptimizationEnabled && cardStatus !== 'ACTIVE'
                        ? 'Paused/Manual'
                        : 'Manual'}
                    </span>
                    {isOptimizationEnabled && cardStatus !== 'ACTIVE' && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        Enable campaign to include in optimization
                      </div>
                    )}
                  </div>
                </div>

                {/* Daily Budget (large). Three branches mirror Google's
                    CampaignCard:748-774:
                    1. Optimizing & ACTIVE → read-only "we will optimize"
                       copy.
                    2. Eligible (CBO + daily) and editable → BudgetBar.
                    3. Ineligible (ABO or lifetime) → static display
                       (BudgetBar would commit a value the optimizer
                       silently excludes — the read-only fallback +
                       FlowerButton tooltip explain why).
                    The min-height keeps card height stable across the
                    three branches. */}
                <div className="mb-4 pl-9">
                  <p className="text-sm text-gray-500 mb-1">{budgetLabelText}</p>
                  <div className="min-h-[68px]" onClick={(e) => e.stopPropagation()}>
                    {isOptimizationEnabled && cardStatus === 'ACTIVE' ? (
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {budgetDisplay}
                        </p>
                        <p className="text-xs text-blue-600 mt-1 min-h-[20px]">
                          We will optimize this for you!
                        </p>
                      </div>
                    ) : isEligibleForOptimization ? (
                      <BudgetBar
                        value={currentManualBudget}
                        // Use the store baseline (`settingsBaselineBudget`)
                        // as BudgetBar's "original" reference so the
                        // BudgetBar's own modified-indicator stays in
                        // sync with this card's `hasStagedBudgetEdit`
                        // and with the post-save promoted value.
                        originalValue={settingsBaselineBudget}
                        onChange={handleBudgetChange}
                        onReset={handleResetBudget}
                        width="w-full"
                      />
                    ) : (
                      <div>
                        <p className="text-2xl font-bold text-gray-900">
                          {budgetDisplay}
                        </p>
                        {optimizationIneligibilityReason && (
                          <p className="text-xs text-gray-500 mt-1 min-h-[20px]">
                            {optimizationIneligibilityReason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Metrics Header */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Last 28 Days</p>
                </div>

                {/* Performance Metrics Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {performanceMetrics.map((metric) => (
                    <div key={metric.label} className="h-10">
                      <p className="text-xs text-gray-500">{metric.label}</p>
                      <p className="text-sm font-medium text-gray-900">{metric.value}</p>
                    </div>
                  ))}
                  <div className="h-10">
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-sm font-medium text-gray-900">
                      ${campaign.metrics.cost.toFixed(2)}
                    </p>
                  </div>
                  <div className="h-10">
                    <p className="text-xs text-gray-500">Conversions</p>
                    <p className="text-sm font-medium text-gray-900">
                      {campaign.metrics.purchases.toFixed(0)}
                    </p>
                  </div>
                </div>

                {/* ROAS — same helper-text matrix as CampaignCard for parity. */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">ROAS (Return on Ad Spend)</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatRoasDisplay(roas)}
                    </p>
                    <div className="min-h-[20px] flex items-center justify-center mt-1">
                      {/* Disclaimer when using click-based ROAS — see
                          CampaignCard for the rationale on branching by
                          `userPickedClickBased` (user intent) rather
                          than `conversionTrackingAvailable` (per-
                          platform data state). Keeps Meta + Google
                          cards aligned when the user has pressed the
                          Click Value radio. */}
                      {roas !== null && isUsingClickBasedRoas && (
                        <p className="text-xs text-amber-600">
                          {userPickedClickBased
                            ? `Click-based · $${(revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK).toFixed(2)}/click`
                            : `No conversion data · $${(revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK).toFixed(2)}/click`}
                        </p>
                      )}
                      {roas === null && !shouldUseConversionBased && campaign.metrics.clicks === 0 && (
                        <p className="text-xs text-gray-400">No clicks yet</p>
                      )}
                      {roas === null && !shouldUseConversionBased && campaign.metrics.clicks > 0 && campaign.metrics.cost === 0 && (
                        <p className="text-xs text-gray-400">No ad spend yet</p>
                      )}
                      {roas === null && shouldUseConversionBased && campaign.metrics.cost === 0 && (
                        <p className="text-xs text-gray-400">No ad spend yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialogs — mirror the Google flow. onConfirm
          callbacks queue through pendingChangesStore with
          `platform: 'meta_ads'`. */}
      <ConfirmDialog
        isOpen={showEnableDialog}
        onClose={() => setShowEnableDialog(false)}
        onConfirm={handleConfirmEnable}
        title="Enable Meta Campaign"
        message={`Are you sure you want to enable "${campaign.campaign_name}"?`}
        confirmText="Enable"
        cancelText="Cancel"
        variant="info"
      />

      <ConfirmDialog
        isOpen={showPauseDialog}
        onClose={() => setShowPauseDialog(false)}
        onConfirm={handleConfirmPause}
        title="Pause Meta Campaign"
        message={`Are you sure you want to pause "${campaign.campaign_name}"?`}
        confirmText="Pause"
        cancelText="Cancel"
        variant="warning"
      />

      <ConfirmDialog
        isOpen={showArchiveDialog}
        onClose={() => setShowArchiveDialog(false)}
        onConfirm={handleConfirmArchive}
        title="Archive Meta Campaign"
        message={`Are you sure you want to archive "${campaign.campaign_name}"? Archived campaigns can be restored later in Meta Ads Manager.`}
        confirmText="Archive"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
}
